/**
 * Servicio HTTP genérico para manejar todas las llamadas GET, POST, PUT, DELETE
 * Centraliza la lógica de comunicación con el backend
 */

export class HttpClientService {
  constructor() {
    this.defaultProxy = 'ServicioTransar.svc';
  }

  /**
   * Realiza una petición GET a un servicio y retorna la respuesta como promesa
   * @param {string} url - URL del endpoint
   * @param {string} proxy - Proxy del servicio (por defecto ServicioTransar.svc)
   * @returns {Promise} - Promesa con la respuesta parseada
   */
  get(url, proxy = this.defaultProxy) {
    return new Promise((resolve, reject) => {
      try {
        const urlToService = new ServicioProxyTransar(url, proxy);
        urlToService
          .getDataFromService('GET', null)
          .then((response) => {
            if (response) {
              const data = JSON.parse(response);
              resolve(data);
            } else {
              reject(new Error('Error en la consulta GET'));
            }
          })
          .catch((error) => {
            console.error('Error en GET:', error);
            reject(error);
          });
      } catch (error) {
        console.error('Error creando HttpClient para GET:', error);
        reject(error);
      }
    });
  }

  /**
   * Realiza una petición POST a un servicio y retorna la respuesta como promesa
   * @param {object} payload - Objeto con los datos a enviar
   * @param {string} url - URL del endpoint
   * @param {string} proxy - Proxy del servicio (por defecto ServicioTransar.svc)
   * @returns {Promise} - Promesa con la respuesta parseada
   */
  post(payload, url, proxy = this.defaultProxy) {
    return new Promise((resolve, reject) => {
      try {
        const urlToService = new ServicioProxyTransar(url, proxy);
        urlToService
          .getElements('POST', payload)
          .then((response) => {
            if (response) {
              const data = typeof response === 'string' ? JSON.parse(response) : response;
              resolve(data);
            } else {
              reject(new Error('Error en la consulta POST'));
            }
          })
          .catch((error) => {
            console.error('Error en POST:', error);
            reject(error);
          });
      } catch (error) {
        console.error('Error creando HttpClient para POST:', error);
        reject(error);
      }
    });
  }

  /**
   * Realiza una petición PUT a un servicio y retorna la respuesta como promesa
   * @param {object} payload - Objeto con los datos a actualizar
   * @param {string} url - URL del endpoint
   * @param {string} proxy - Proxy del servicio (por defecto ServicioTransar.svc)
   * @returns {Promise} - Promesa con la respuesta parseada
   */
  put(payload, url, proxy = this.defaultProxy) {
    return new Promise((resolve, reject) => {
      try {
        const urlToService = new ServicioProxyTransar(url, proxy);
        urlToService
          .getElements('PUT', payload)
          .then((response) => {
            if (response) {
              const data = typeof response === 'string' ? JSON.parse(response) : response;
              resolve(data);
            } else {
              reject(new Error('Error en la consulta PUT'));
            }
          })
          .catch((error) => {
            console.error('Error en PUT:', error);
            reject(error);
          });
      } catch (error) {
        console.error('Error creando HttpClient para PUT:', error);
        reject(error);
      }
    });
  }

  /**
   * Realiza una petición DELETE a un servicio y retorna la respuesta como promesa
   * @param {string} url - URL del endpoint
   * @param {string} proxy - Proxy del servicio (por defecto ServicioTransar.svc)
   * @returns {Promise} - Promesa con la respuesta parseada
   */
  delete(url, proxy = this.defaultProxy) {
    return new Promise((resolve, reject) => {
      try {
        const urlToService = new ServicioProxyTransar(url, proxy);
        urlToService
          .getElements('DELETE', null)
          .then((response) => {
            if (response) {
              const data = typeof response === 'string' ? JSON.parse(response) : response;
              resolve(data);
            } else {
              reject(new Error('Error en la consulta DELETE'));
            }
          })
          .catch((error) => {
            console.error('Error en DELETE:', error);
            reject(error);
          });
      } catch (error) {
        console.error('Error creando HttpClient para DELETE:', error);
        reject(error);
      }
    });
  }

  /**
   * Verifica si la respuesta es exitosa (ResponseCode 200)
   * @param {object} response - Respuesta del servidor
   * @returns {boolean}
   */
  isSuccess(response) {
    return response && response.ResponseCode === 200;
  }

  /**
   * Verifica si es un error de servidor (500, 450)
   * @param {object} response - Respuesta del servidor
   * @returns {boolean}
   */
  isServerError(response) {
    return response && (response.ResponseCode === 500 || response.ResponseCode === 450);
  }

  /**
   * Verifica si es un error de validación (430)
   * @param {object} response - Respuesta del servidor
   * @returns {boolean}
   */
  isValidationError(response) {
    return response && response.ResponseCode === 430;
  }
}

// Singleton instance
export const httpClient = new HttpClientService();
